/**
 * The gate in front of the staff portal.
 *
 * Three states, and conflating any two of them produces a bug somebody will
 * report as "it logged me out":
 *
 *   loading   the stored token has not been checked yet. Render nothing much —
 *             showing the sign-in form here flashes a login screen at somebody
 *             who is already signed in.
 *   no user   send them to sign in, remembering where they were going.
 *   volunteer signed in, but somewhere else. Sent to their own page — which
 *             exists now, so there is nothing to explain.
 *
 * This is a convenience, not the control. The API refuses a volunteer on every
 * staff route regardless of what the browser renders (D21) — this exists so
 * that refusal arrives as a sentence instead of a wall of failed panels.
 */

import { Navigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { LoadingState } from '../components/common';
import { useAuth } from '../hooks/useAuth';

export function RequireStaff({ children }: { children: ReactNode }) {
  const { user, loading, isStaff } = useAuth();

  if (loading) return <LoadingState label="Checking your session…" />;

  if (!user) {
    // No `state.from`. It used to carry the page being guarded, and the
    // sign-in page sent you back to it — which meant the next session
    // inherited the last one’s position. Signing in lands on `/`.
    return <Navigate to="/login" replace />;
  }

  // A volunteer is not lost, they are simply somewhere else. "/" is the staff
  // dashboard and also a volunteer's landing point, so the redirect is what
  // makes one URL work for both.
  if (!isStaff) return <Navigate to="/my-volunteering" replace />;

  return <>{children}</>;
}
